import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '../services/authService';
import { socialAuthService } from '../services/socialAuthService';
import { notificationService } from '../services/notificationService';
import { api, API_ENDPOINTS } from '../lib/api';
import { User, LoginRequest, RegisterRequest, AuthResponse } from '../types/auth';
import { handleApiError, formatErrorForDisplay, captureError } from '../lib/errorHandler';
import { setSentryUser, clearSentryUser, addBreadcrumb } from '../lib/sentry';

// Validate that an auth response carries the user/token fields we need before
// state is committed. Throws a typed error if anything is missing — the catch
// block above re-shapes it for the user-facing message, and Sentry receives a
// scrubbed snapshot of the actual response shape so we can diagnose remotely.
function assertAuthResponseShape(
  response: AuthResponse | undefined,
  context: string,
  extra?: Record<string, any>,
): void {
  if (response && response.user && response.access_token) return;

  const responseShape = response
    ? {
        keys: Object.keys(response),
        hasUser: !!response.user,
        userKeys: response.user ? Object.keys(response.user) : null,
        hasAccessToken: !!response.access_token,
        hasRefreshToken: !!response.refresh_token,
        hasExpiresIn: typeof response.expires_in === 'number',
      }
    : { keys: null, response: response === undefined ? 'undefined' : 'null' };

  const err = new Error(
    `Auth response missing required fields (${context}): ` +
      `${!response ? 'response is null/undefined' : !response.user ? 'no user' : 'no access_token'}`,
  );
  captureError(err, context, { responseShape, ...extra });
  throw err;
}
import { useNotificationStore } from './notificationStore';

interface AuthState {
  // State
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  tokenExpiresAt: number | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (credentials: LoginRequest) => Promise<void>;  // credentials.identifier = email or phone
  register: (userData: RegisterRequest) => Promise<{ walletCreated?: boolean }>;
  logout: () => void;
  loadProfile: () => Promise<void>;
  updateProfile: (
    userData: Partial<Omit<User, 'id' | 'role' | 'isActive' | 'createdAt' | 'updatedAt'>>
  ) => Promise<void>;
  socialLogin: (provider: 'google' | 'facebook' | 'apple') => Promise<{ isNew: boolean }>;
  phoneOtpLogin: (idToken: string) => Promise<void>;
  setUser: (user: User) => void;
  clearError: () => void;
  setTokens: (accessToken: string, refreshToken: string, expiresIn: number) => void;
  getRefreshToken: () => string | null;
}

/**
 * Authentication Store
 * Manages user authentication state with persistence to localStorage
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      refreshToken: null,
      tokenExpiresAt: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Login action
      login: async (credentials: LoginRequest) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authService.login(credentials);
          assertAuthResponseShape(response, 'auth.login');

          // Calculate token expiration time
          const tokenExpiresAt = Date.now() + response.expires_in * 1000;

          set({
            user: response.user,
            token: response.access_token,
            refreshToken: response.refresh_token,
            tokenExpiresAt,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
          setSentryUser(response.user);
          addBreadcrumb('auth', 'Login successful');
        } catch (error) {
          const appError = handleApiError(error);
          set({
            user: null,
            token: null,
            refreshToken: null,
            tokenExpiresAt: null,
            isAuthenticated: false,
            isLoading: false,
            error: formatErrorForDisplay(appError),
          });
          throw error;
        }
      },

      // Register action
      register: async (userData: RegisterRequest) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authService.register(userData);

          // Calculate token expiration time
          const tokenExpiresAt = Date.now() + response.expires_in * 1000;

          set({
            user: response.user,
            token: response.access_token,
            refreshToken: response.refresh_token,
            tokenExpiresAt,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
          setSentryUser(response.user);
          addBreadcrumb('auth', 'Registration successful');
          return { walletCreated: response.walletCreated };
        } catch (error) {
          const appError = handleApiError(error);
          set({
            user: null,
            token: null,
            refreshToken: null,
            tokenExpiresAt: null,
            isAuthenticated: false,
            isLoading: false,
            error: formatErrorForDisplay(appError),
          });
          throw error;
        }
      },

      // Logout action
      logout: () => {
        addBreadcrumb('auth', 'User logged out');
        clearSentryUser();

        // Capture tokens before clearing state — API calls need the JWT
        const fcmToken = useNotificationStore.getState().fcmToken;
        const authToken = get().token;

        // Reset notification store
        useNotificationStore.getState().reset();

        // Clear state
        set({
          user: null,
          token: null,
          refreshToken: null,
          tokenExpiresAt: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });

        // Unregister FCM token from backend (fire-and-forget, uses captured auth token)
        if (fcmToken && authToken) {
          import('axios').then(({ default: axios }) => {
            const baseUrl = import.meta.env.VITE_API_BASE_URL;
            axios.delete(`${baseUrl}/notifications/devices/${encodeURIComponent(fcmToken)}`, {
              headers: { Authorization: `Bearer ${authToken}` },
            }).catch((err) => {
              console.warn('[Auth] FCM token unregister failed:', err);
            });
          });
        }

        // Call backend logout endpoint (optional, fire and forget)
        authService.logout().catch((err) => {
          console.warn('Backend logout failed:', err);
        });
      },

      // Load user profile
      loadProfile: async () => {
        const { token } = get();

        // Don't load if no token
        if (!token) {
          return;
        }

        set({ isLoading: true, error: null });

        try {
          const user = await authService.getProfile();

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const appError = handleApiError(error);

          // If 401, logout
          if (appError.statusCode === 401) {
            get().logout();
          } else {
            set({
              isLoading: false,
              error: formatErrorForDisplay(appError),
            });
          }

          throw error;
        }
      },

      // Update user profile
      updateProfile: async (
        userData: Partial<Omit<User, 'id' | 'role' | 'isActive' | 'createdAt' | 'updatedAt'>>
      ) => {
        const { token } = get();

        // Don't update if no token
        if (!token) {
          throw new Error('Not authenticated');
        }

        // NOTE: Do NOT set global isLoading here - it causes ProtectedLayout to show spinner
        // and unmount all children including any open sheets/modals
        set({ error: null });

        try {
          const updatedUser = await authService.updateProfile(userData);

          set({
            user: updatedUser,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const appError = handleApiError(error);
          const formattedMessage = formatErrorForDisplay(appError);

          // If 401, logout
          if (appError.statusCode === 401) {
            get().logout();
          } else {
            set({
              error: formattedMessage,
            });
          }

          // Throw enhanced error with formatted message instead of raw axios error
          // This ensures components receive the backend error message
          const enhancedError = new Error(formattedMessage);
          (enhancedError as any).originalError = error;
          (enhancedError as any).statusCode = appError.statusCode;

          console.error('[AuthStore] updateProfile error:', {
            message: formattedMessage,
            statusCode: appError.statusCode,
            originalError: error,
          });

          throw enhancedError;
        }
      },

      // Social login action (Google or Facebook)
      // NOTE: Do NOT set global isLoading:true here — it causes ProtectedLayout to show
      // full-screen spinner and unmount children. Use local loadingProvider in SocialAuthButtons.
      socialLogin: async (provider: 'google' | 'facebook' | 'apple') => {
        set({ error: null });
        try {
          const signInFn = {
            google: socialAuthService.signInWithGoogle,
            facebook: socialAuthService.signInWithFacebook,
            apple: socialAuthService.signInWithApple,
          }[provider];
          const response = await signInFn();
          assertAuthResponseShape(response, 'auth.socialLogin', { provider });

          const tokenExpiresAt = Date.now() + response.expires_in * 1000;
          set({
            user: response.user,
            token: response.access_token,
            refreshToken: response.refresh_token,
            tokenExpiresAt,
            isAuthenticated: true,
            error: null,
          });
          setSentryUser(response.user);
          addBreadcrumb('auth', 'Social login successful', { provider });
          // isNew is ephemeral — return it directly, do NOT store in Zustand state
          return { isNew: response.isNew ?? false };
        } catch (error) {
          const appError = handleApiError(error);
          set({
            user: null,
            token: null,
            refreshToken: null,
            tokenExpiresAt: null,
            isAuthenticated: false,
            error: formatErrorForDisplay(appError),
          });
          throw error;
        }
      },

      // Phone OTP login — exchange Firebase Phone Auth token for app JWT
      phoneOtpLogin: async (idToken: string) => {
        set({ error: null });
        try {
          const response = await api.post<AuthResponse>(API_ENDPOINTS.AUTH.SOCIAL_LOGIN, {
            idToken,
            provider: 'phone',
          });
          assertAuthResponseShape(response.data, 'auth.phoneOtpLogin');
          const tokenExpiresAt = Date.now() + response.data.expires_in * 1000;
          set({
            user: response.data.user,
            token: response.data.access_token,
            refreshToken: response.data.refresh_token,
            tokenExpiresAt,
            isAuthenticated: true,
            error: null,
          });
          setSentryUser(response.data.user);
          addBreadcrumb('auth', 'Phone OTP login successful');
        } catch (error) {
          const appError = handleApiError(error);
          set({
            error: formatErrorForDisplay(appError),
          });
          throw error;
        }
      },

      // Set user directly (used when fetching fresh user data)
      setUser: (user: User) => {
        set({ user });
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },

      // Set tokens after refresh (used by API interceptor)
      setTokens: (accessToken: string, refreshToken: string, expiresIn: number) => {
        const tokenExpiresAt = Date.now() + expiresIn * 1000;
        set({
          token: accessToken,
          refreshToken,
          tokenExpiresAt,
        });
      },

      // Get refresh token (used by API interceptor)
      getRefreshToken: () => {
        return get().refreshToken;
      },
    }),
    {
      name: 'auth-storage', // localStorage key
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        tokenExpiresAt: state.tokenExpiresAt,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

/**
 * Listen for auth:logout custom event from API interceptor
 * This handles automatic logout on 401 responses
 */
if (typeof window !== 'undefined') {
  window.addEventListener('auth:logout', () => {
    useAuthStore.getState().logout();
  });
}
